import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { makeRequest2 } from './makeRequest';
import { recordLandlordWalletPayment, getWalletBalanceURL } from './urls';
import { toastify } from './toast';
import { v4 as uuidv4 } from 'uuid';

const LandlordWalletModal = ({
    visible,
    onHide,
    invoice,
    facilityId,
    onSuccess,
    onError
}) => {
    const [loading, setLoading] = useState(false);
    const [walletBalance, setWalletBalance] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);
    const [formData, setFormData] = useState({
        amount: 0,
        receiptNumber: `WALLET-${uuidv4().substring(0, 8)}`,
        paymentDate: new Date().toISOString().split('T')[0],
        notes: ''
    });

    // Levy/service-charge invoices bill the homeowner directly, so
    // invoice.client.clientId here IS the wallet owner.
    const walletOwnerId = invoice?.client?.clientId;

    const getRemainingBalance = () => {
        if (!invoice) return 0;
        return (invoice.totalAmount || 0) + (invoice.balanceBroughtForward || 0) - (invoice?.amountPaid || 0);
    };

    // Reset form + prefill amount whenever the modal is opened for a new invoice
    useEffect(() => {
        if (visible && invoice) {
            setFormData({
                amount: getRemainingBalance(),
                receiptNumber: `WALLET-${uuidv4().substring(0, 8)}`,
                paymentDate: new Date().toISOString().split('T')[0],
                notes: ''
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, invoice]);

    // Fetch the current wallet balance whenever the modal opens
    useEffect(() => {
        const fetchWalletBalance = async () => {
            if (!visible || !walletOwnerId || !facilityId) return;
            try {
                setWalletLoading(true);
                setWalletBalance(null);
                const res = await makeRequest2(
                    `${getWalletBalanceURL}/${facilityId}/${walletOwnerId}`,
                    'GET',
                    {}
                );
                const bal = res?.data?.data?.balance ?? res?.data?.balance ?? 0;
                setWalletBalance(bal);
            } catch (err) {
                console.warn('Failed to fetch wallet balance:', err.message);
                setWalletBalance(null);
            } finally {
                setWalletLoading(false);
            }
        };
        fetchWalletBalance();
    }, [visible, walletOwnerId, facilityId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const isInsufficient =
        walletBalance !== null && parseFloat(formData.amount || 0) > walletBalance;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (!facilityId || !invoice?._id) {
                throw new Error('Missing required payment details');
            }

            if (parseFloat(formData.amount) <= 0) {
                throw new Error('Payment amount must be greater than zero');
            }

            // Core check requested: never let a wallet payment exceed what's
            // actually available in the wallet. Re-checked here (not just via
            // the disabled button) in case the balance changed while the
            // modal was open.
            if (walletBalance === null) {
                throw new Error('Could not verify wallet balance. Please try again.');
            }
            if (parseFloat(formData.amount) > walletBalance) {
                throw new Error(
                    `Insufficient wallet balance. Available: KES ${walletBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                );
            }

            const payload = {
                invoiceNumber: invoice.invoiceNumber,
                amount: parseFloat(formData.amount),
                receiptNumber: formData.receiptNumber,
                paymentMethod: 'Wallet',
                paymentDate: formData.paymentDate,
                notes: formData.notes,
                currencyId: invoice?.currency?.id
            };

            const response = await makeRequest2(
                `${recordLandlordWalletPayment}/${facilityId}`,
                'POST',
                payload,
                false // JSON request
            );

            if (!response.success) {
                throw new Error(response.message || 'Failed to record wallet payment');
            }

            toastify('Wallet payment recorded successfully. Pending approval.', 'success');
            if (onSuccess) onSuccess(response.payment);

            setTimeout(onHide, 2000);
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Wallet payment failed. Please try again.';
            toastify(message, 'error');
            if (onError) onError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header="Pay with Landlord Wallet"
            modal
            className="p-fluid"
            style={{ width: '450px' }}
            dismissableMask={!loading}
            closeOnEscape={!loading}
        >
            <form onSubmit={handleSubmit}>
                {/* Wallet balance display */}
                <div className="form-group mb-3">
                    {walletLoading ? (
                        <small className="text-muted">Checking wallet balance...</small>
                    ) : walletBalance === null ? (
                        <small className="text-danger">Could not load wallet balance.</small>
                    ) : (
                        <div className={`p-2 rounded ${isInsufficient ? 'bg-danger bg-opacity-10' : 'bg-success bg-opacity-10'}`}>
                            <small className={isInsufficient ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>
                                Available Wallet Balance: KES {walletBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </small>
                            {isInsufficient && (
                                <div>
                                    <small className="text-danger">
                                        This amount exceeds the available wallet balance.
                                    </small>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="amount">
                        Amount ({invoice?.currency?.code || 'KES'})
                    </label>
                    <input
                        type="number"
                        className="form-control"
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        disabled={loading}
                        required
                        step="0.01"
                        min="0.01"
                        max={walletBalance !== null ? walletBalance : undefined}
                    />
                    <small className="text-muted">
                        Balance due: {invoice?.currency?.code || 'KES'} {getRemainingBalance().toFixed(2)}
                    </small>
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="receiptNumber">Receipt Number</label>
                    <input
                        type="text"
                        className="form-control"
                        id="receiptNumber"
                        name="receiptNumber"
                        value={formData.receiptNumber}
                        onChange={handleInputChange}
                        disabled
                        required
                    />
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="paymentDate">Payment Date</label>
                    <input
                        type="date"
                        className="form-control"
                        id="paymentDate"
                        name="paymentDate"
                        value={formData.paymentDate}
                        onChange={handleInputChange}
                        disabled={loading}
                        required
                        max={new Date().toISOString().split('T')[0]}
                    />
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="notes">Notes</label>
                    <InputTextarea
                        className="form-control"
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        disabled={loading}
                        rows={3}
                        autoResize={false}
                    />
                </div>

                <div className="d-flex justify-content-end mt-4">
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={onHide}
                        className="p-button-text btn btn-secondary me-2"
                        disabled={loading}
                        type="button"
                    />
                    <Button
                        label={loading ? 'Processing...' : 'Record Payment'}
                        icon="pi pi-check"
                        type="submit"
                        disabled={loading || walletLoading || isInsufficient || walletBalance === null}
                        className="btn btn-primary"
                    />
                </div>
            </form>
        </Dialog>
    );
};

export default LandlordWalletModal;